import { colors, Link } from '@mui/material';
import { styled } from '@mui/material/styles';

export const NewsList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

export const NewsItem = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  '&:last-child': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
}));

export const NewsTitle = styled(Link)(({ theme }) => ({
  fontSize: '0.85rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  lineHeight: 1.4,
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

export const NewsMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: '0.72rem',
  color: theme.palette.text.secondary,
}));

export const MetaDot = styled('span')({
  color: colors.grey[400],
});

export const NewsSummary = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: '0.78rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

export const ArticleList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  marginTop: theme.spacing(0.5),
}));

export const ArticleLink = styled(Link)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const ArticleMeta = styled('span')(({ theme }) => ({
  fontSize: '0.72rem',
  color: theme.palette.text.secondary,
}));

export const PaginationRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
}));

export const TabRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  paddingBottom: theme.spacing(0.5),
}));

interface TabButtonProps {
  active?: boolean;
}

export const TabButton = styled('button')<TabButtonProps>(({ theme, active }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  fontSize: '0.8rem',
  fontWeight: active ? 700 : 400,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  borderBottom: active ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  marginBottom: '-1px',
  transition: 'color 0.15s, border-color 0.15s',
  '&:hover': {
    color: theme.palette.primary.main,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    color: theme.palette.text.disabled,
  },
}));

export const CategoryChips = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1.5),
}));

interface ChipButtonProps {
  active?: boolean;
}

export const ChipButton = styled('button')<ChipButtonProps>(({ theme, active }) => ({
  background: active ? theme.palette.primary.main : theme.palette.grey[100],
  color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: '12px',
  cursor: 'pointer',
  fontSize: '0.7rem',
  padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
  fontWeight: active ? 600 : 400,
  transition: 'background 0.15s, color 0.15s',
  '&:hover': {
    background: active ? theme.palette.primary.dark : theme.palette.grey[200],
  },
}));

export const ExpandableText = styled('p')<{ expanded?: boolean }>(({ expanded }) => ({
  margin: 0,
  fontSize: '0.78rem',
  lineHeight: 1.5,
  display: expanded ? 'block' : '-webkit-box',
  WebkitLineClamp: expanded ? 'unset' : 3,
  WebkitBoxOrient: 'vertical',
  overflow: expanded ? 'visible' : 'hidden',
}));

export const ExpandToggle = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: theme.palette.primary.main,
  fontSize: '0.72rem',
  padding: 0,
  marginTop: theme.spacing(0.25),
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.primary.dark,
  },
}));

export const ItemSource = styled('span')(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

export const TransactionBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  fontSize: '0.65rem',
  color: theme.palette.text.disabled,
  cursor: 'pointer',
  opacity: 0.5,
  transition: 'opacity 0.15s',
  '&:hover': {
    opacity: 1,
    color: theme.palette.primary.main,
  },
}));

export const TransactionIdCode = styled('code')(({ theme }) => ({
  fontSize: '0.7rem',
  fontFamily: 'monospace',
  background: theme.palette.grey[100],
  padding: '1px 4px',
  borderRadius: 3,
  color: theme.palette.text.secondary,
}));
